import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileArrowUp, faTrash } from "@fortawesome/free-solid-svg-icons";
import SectionHeader from "./SectionHeader";
import { carrierOptions } from "../presentation";
import { formatSaveProgress, getProcessedItemRowCount } from "../saveProgress";

const STATUS_BOX = "mt-3 rounded-xl border px-3 py-2.5 text-xs";

function borderColorFor(processedItem) {
  if (processedItem?.error) return "border-l-red-400";
  if (processedItem?.saveSuccess === false) return "border-l-amber-400";
  if (processedItem) return "border-l-emerald-400";
  return "border-l-amber-300";
}

function FileStatus({ processedItem }) {
  const parsedRowCount = getProcessedItemRowCount(processedItem);
  const saveProgressLabel = formatSaveProgress(processedItem);

  if (processedItem?.error) {
    return (
      <div className={`${STATUS_BOX} border-red-200 bg-red-50 text-red-700`}>
        {processedItem.error}
      </div>
    );
  }

  if (processedItem?.saveSuccess === null) {
    return (
      <div className={`${STATUS_BOX} border-blue-200 bg-blue-50 text-blue-700`}>
        Parsed <span className="font-semibold">{parsedRowCount} source rows</span>. Not
        saved yet.
      </div>
    );
  }

  if (processedItem?.saveSuccess === false) {
    return (
      <div className={`${STATUS_BOX} border-amber-200 bg-amber-50 text-amber-700`}>
        {saveProgressLabel ? (
          <>
            <span className="font-semibold">{saveProgressLabel}</span>.{" "}
          </>
        ) : (
          <>
            Parsed <span className="font-semibold">{parsedRowCount} source rows</span>.{" "}
          </>
        )}
        {processedItem.saveMessage || "Snowflake save failed."}
      </div>
    );
  }

  if (processedItem) {
    return (
      <div className={`${STATUS_BOX} border-emerald-200 bg-emerald-50 text-emerald-700`}>
        <span className="font-semibold">
          {saveProgressLabel || `${parsedRowCount} source rows saved`}
        </span>
      </div>
    );
  }

  return (
    <div className={`${STATUS_BOX} border-amber-200 bg-amber-50 text-amber-700`}>
      Ready to process.
    </div>
  );
}

function UploadedFileCard({ item, processedItem, onCarrierChange, onRemove }) {
  return (
    <div
      className={`rounded-2xl border border-slate-200 border-l-4 ${borderColorFor(
        processedItem
      )} bg-slate-50 p-4 transition-all`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900">
            {item.file.name}
          </p>
          <p className="mt-0.5 text-xs text-slate-400">
            {(item.file.size / 1024 / 1024).toFixed(2)} MB
          </p>
        </div>
        <button
          type="button"
          onClick={() => onRemove(item.id)}
          className="flex-shrink-0 rounded-lg p-1.5 text-slate-400 transition hover:bg-white hover:text-red-500"
          aria-label="Remove file"
        >
          <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="mt-3">
        <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-slate-400">
          Carrier
        </label>
        <select
          value={item.selectedCarrier}
          onChange={(event) => onCarrierChange(item.id, event.target.value)}
          className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-[#144677] focus:ring-1 focus:ring-[#144677]/20"
        >
          {carrierOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <FileStatus processedItem={processedItem} />
    </div>
  );
}

export default function UploadedFilesList({
  uploadedFiles,
  processedFiles,
  onCarrierChange,
  onRemove,
}) {
  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <SectionHeader
        icon={faFileArrowUp}
        title="Uploaded Files"
        subtitle="Preview runs automatically on upload and carrier changes."
        badge={
          uploadedFiles.length > 0 ? (
            <span className="flex-shrink-0 rounded-full bg-slate-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-slate-600">
              {uploadedFiles.length} {uploadedFiles.length === 1 ? "file" : "files"}
            </span>
          ) : null
        }
      />

      <div className="space-y-3">
        {uploadedFiles.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 py-12 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100">
              <FontAwesomeIcon icon={faFileArrowUp} className="text-xl text-slate-400" />
            </div>
            <p className="text-sm text-slate-500">No files uploaded yet.</p>
          </div>
        ) : (
          uploadedFiles.map((item) => (
            <UploadedFileCard
              key={item.id}
              item={item}
              processedItem={processedFiles.find(
                (file) => file.fileName === item.file.name
              )}
              onCarrierChange={onCarrierChange}
              onRemove={onRemove}
            />
          ))
        )}
      </div>
    </section>
  );
}
